import { type SystemConfig, SystemConfigKey } from '../entity/api/system-config';
import { trackError } from '../error/tracking';
import { captureException } from '../logger/capture-exception';
import { getSystemConfigsFromCache } from '../services/storage/system-config-queries';

export enum Feature {
  ONBOARDING = 'onboarding',
  BRAND_FILLER = 'brand-filler',
  PRODUCT = 'product',
  SUBSCRIPTION = 'subscription',
  ONE_CLICK_GENERATION = 'one-click-generation',
  INTEGRATIONS = 'integrations',
  GEO_INFORMATION = 'geo-information',
  SKIPABLE_ONBOARDING = 'skipable-onboarding',
  FUNNEL_STEP_ONE_V2 = 'funnel-step-one-v2',
  REWRITE_CONTENT = 'rewrite-content',
  CREDIT_PAYMENT_AVAILABLE = 'credit-payment-available',
  AUDIT = 'audit',
  SHOPIFY_INTEGRATION = 'shopify-integration',
  BLOGGLE_INTEGRATION = 'bloggle-integration',
  WEBPAGE_CONTENT = 'webpage-content',
}

export enum FeatureFlag {
  ONBOARDING = 'isOnboardingAvailable',
  PRODUCT = 'isProductAvailable',
  SUBSCRIPTION = 'isSubscriptionAvailable',
  BRAND_FILLER = 'isBrandFillerAvailable',
  ONE_CLICK_GENERATION = 'isOneClickGenerationAvailable',
  INTEGRATIONS = 'isIntegrationsAvailable',
  GEO_INFORMATION = 'isGeoInformationAvailable',
  SKIPABLE_ONBOARDING = 'isSkipableOnboardingAvailable',
  FUNNEL_STEP_ONE_V2 = 'isFunnelStepOneV2Available',
  REWRITE_CONTENT = 'isRewriteContentAvailable',
  CREDIT_PAYMENT_AVAILABLE = 'isCreditPaymentAvailable',
  AUDIT = 'isAuditAvailable',
  SHOPIFY_INTEGRATION = 'isShopifyIntegrationAvailable',
  BLOGGLE_INTEGRATION = 'isBloggleIntegrationAvailable',
  WEBPAGE_CONTENT = 'isWebPageContentAvailable',
}

export const isFeatureAvailable = (feature: Feature) => {
  let featureFlags: Partial<Record<FeatureFlag, boolean>> = {};
  const configs = getSystemConfigsFromCache();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const featureConfig = configs?.find(
    (config: SystemConfig) => config?.key === SystemConfigKey.Features
  );

  try {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
    featureFlags =
      (JSON.parse(featureConfig?.value as string) as Record<FeatureFlag, boolean>) ?? {};
  } catch (err) {
    captureException(
      err,
      {
        file: 'feature.ts',
        method: 'isFeatureAvailable',
        infoMessage: 'Error parsing feature flags',
      },
      trackError
    );
    featureFlags = {};
  }
  switch (feature) {
    case Feature.PRODUCT:
      return typeof featureFlags?.[FeatureFlag.PRODUCT] === 'boolean'
        ? featureFlags?.[FeatureFlag.PRODUCT]
        : false;
    case Feature.SUBSCRIPTION:
      return typeof featureFlags?.[FeatureFlag.SUBSCRIPTION] === 'boolean'
        ? featureFlags?.[FeatureFlag.SUBSCRIPTION]
        : true; // Longterm feature
    case Feature.BRAND_FILLER:
      return typeof featureFlags?.[FeatureFlag.BRAND_FILLER] === 'boolean'
        ? featureFlags?.[FeatureFlag.BRAND_FILLER]
        : true; // Longterm feature
    case Feature.ONE_CLICK_GENERATION:
      return typeof featureFlags?.[FeatureFlag.ONE_CLICK_GENERATION] === 'boolean'
        ? featureFlags?.[FeatureFlag.ONE_CLICK_GENERATION]
        : false;
    case Feature.ONBOARDING:
      return typeof featureFlags?.[FeatureFlag.ONBOARDING] === 'boolean'
        ? featureFlags?.[FeatureFlag.ONBOARDING]
        : false;
    case Feature.INTEGRATIONS:
      return typeof featureFlags?.[FeatureFlag.INTEGRATIONS] === 'boolean'
        ? featureFlags?.[FeatureFlag.INTEGRATIONS]
        : false;
    case Feature.GEO_INFORMATION:
      return typeof featureFlags?.[FeatureFlag.GEO_INFORMATION] === 'boolean'
        ? featureFlags?.[FeatureFlag.GEO_INFORMATION]
        : false;
    case Feature.SKIPABLE_ONBOARDING:
      return typeof featureFlags?.[FeatureFlag.SKIPABLE_ONBOARDING] === 'boolean'
        ? featureFlags?.[FeatureFlag.SKIPABLE_ONBOARDING]
        : false;
    case Feature.FUNNEL_STEP_ONE_V2:
      return typeof featureFlags?.[FeatureFlag.FUNNEL_STEP_ONE_V2] === 'boolean'
        ? featureFlags?.[FeatureFlag.FUNNEL_STEP_ONE_V2]
        : false;
    case Feature.REWRITE_CONTENT:
      return typeof featureFlags?.[FeatureFlag.REWRITE_CONTENT] === 'boolean'
        ? featureFlags?.[FeatureFlag.REWRITE_CONTENT]
        : false;
    case Feature.CREDIT_PAYMENT_AVAILABLE:
      return typeof featureFlags?.[FeatureFlag.CREDIT_PAYMENT_AVAILABLE] === 'boolean'
        ? featureFlags?.[FeatureFlag.CREDIT_PAYMENT_AVAILABLE]
        : false;
    case Feature.AUDIT:
      return typeof featureFlags?.[FeatureFlag.AUDIT] === 'boolean'
        ? featureFlags?.[FeatureFlag.AUDIT]
        : false;
    case Feature.SHOPIFY_INTEGRATION:
      return typeof featureFlags?.[FeatureFlag.SHOPIFY_INTEGRATION] === 'boolean'
        ? featureFlags?.[FeatureFlag.SHOPIFY_INTEGRATION]
        : false;
    case Feature.BLOGGLE_INTEGRATION:
      return typeof featureFlags?.[FeatureFlag.BLOGGLE_INTEGRATION] === 'boolean'
        ? featureFlags?.[FeatureFlag.BLOGGLE_INTEGRATION]
        : false;
    case Feature.WEBPAGE_CONTENT:
      return typeof featureFlags?.[FeatureFlag.WEBPAGE_CONTENT] === 'boolean'
        ? featureFlags?.[FeatureFlag.WEBPAGE_CONTENT]
        : false;
    default:
      return false;
  }
};
